import { useEffect, useState } from 'react';

import { formatCount, formatMoney, formatPeriodLabel } from '../../../utils/salesReportUtils';

const METRICS = [
    { id: 'netSales', label: 'Neto', money: true },
    { id: 'grossSales', label: 'Bruto', money: true },
    { id: 'orderCount', label: 'Pedidos', money: false },
    { id: 'unitsSold', label: 'Unidades', money: false },
];

const GROUP_NOUN = {
    day: 'día',
    week: 'semana',
    month: 'mes',
};

const SalesReportChart = ({ series = [], groupBy = 'day' }) => {
    const [metricId, setMetricId] = useState('netSales');
    const metric = METRICS.find((item) => item.id === metricId) || METRICS[0];
    const lastIndex = Math.max(series.length - 1, 0);
    const [activeIndex, setActiveIndex] = useState(lastIndex);
    const seriesKey = `${series[0]?.period || ''}:${series.length}:${series[series.length - 1]?.period || ''}`;

    useEffect(() => {
        setActiveIndex(lastIndex);
    }, [seriesKey, lastIndex]);

    if (!series.length) {
        return (
            <section className="sr-chart-card">
                <h2>Serie</h2>
                <p className="sr-empty mb-0">No hay periodos en este rango.</p>
            </section>
        );
    }

    const values = series.map((point) => Number(point[metric.id]) || 0);
    const peak = Math.max(...values, 0);
    const top = peak === 0 ? 1 : peak;
    const height = 240;
    const pad = { top: 16, right: 12, bottom: 34, left: 58 };
    const slot = series.length > 48 ? 12 : 22;
    const width = Math.max(series.length * slot, 320) + pad.left + pad.right;
    const innerW = width - pad.left - pad.right;
    const innerH = height - pad.top - pad.bottom;
    const gap = series.length > 40 ? 2 : 5;
    const barW = Math.max((innerW - gap * Math.max(series.length - 1, 0)) / series.length, 3);
    const labelStep = Math.max(1, Math.ceil(series.length / 6));
    const ticks = [top, top / 2, 0];
    const active = series[Math.min(activeIndex, series.length - 1)] || series[0];
    const formatValue = (value) => (metric.money ? formatMoney(value) : formatCount(value));

    const moveActive = (next) => {
        setActiveIndex(Math.max(0, Math.min(series.length - 1, next)));
    };

    return (
        <section className="sr-chart-card" aria-label={`Serie de ${metric.label.toLowerCase()} por ${GROUP_NOUN[groupBy] || 'periodo'}`}>
            <div className="sr-section-head">
                <div>
                    <h2>
                        {metric.label} por {GROUP_NOUN[groupBy] || 'periodo'}
                    </h2>
                    <p className="sr-hint mb-0">Usa las flechas del teclado para recorrer la serie.</p>
                </div>
                <div className="sr-metric-switch" role="group" aria-label="Métrica de la serie">
                    {METRICS.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            className={item.id === metric.id ? 'is-active' : ''}
                            aria-pressed={item.id === metric.id}
                            onClick={() => setMetricId(item.id)}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            </div>

            <div
                className="sr-chart-scroll"
                tabIndex={0}
                onKeyDown={(event) => {
                    if (event.key === 'ArrowRight') {
                        event.preventDefault();
                        moveActive(activeIndex + 1);
                    }
                    if (event.key === 'ArrowLeft') {
                        event.preventDefault();
                        moveActive(activeIndex - 1);
                    }
                }}
            >
                <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', minWidth: width }} aria-hidden="true">
                    {ticks.map((tick) => {
                        const y = pad.top + innerH - (tick / top) * innerH;
                        return (
                            <g key={tick}>
                                <line x1={pad.left} x2={width - pad.right} y1={y} y2={y} stroke="#eadfd8" />
                                <text x={pad.left - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#6d5c54">
                                    {metric.money ? formatCompactMoney(tick) : formatCount(tick)}
                                </text>
                            </g>
                        );
                    })}
                    {series.map((point, index) => {
                        const value = values[index];
                        const barHeight = value === 0 ? 2 : Math.max((value / top) * innerH, 2);
                        const x = pad.left + index * (barW + gap);
                        const y = pad.top + innerH - barHeight;
                        const selected = index === activeIndex;
                        return (
                            <g key={point.period || index}>
                                <rect
                                    x={x}
                                    y={y}
                                    width={barW}
                                    height={barHeight}
                                    rx="3"
                                    fill={value === 0 ? '#eadfd8' : selected ? '#8c330c' : '#b5430f'}
                                    onClick={() => setActiveIndex(index)}
                                />
                                {(index % labelStep === 0 || index === series.length - 1) && (
                                    <text
                                        x={x + barW / 2}
                                        y={height - 8}
                                        textAnchor="middle"
                                        fontSize="11"
                                        fill="#6d5c54"
                                    >
                                        {formatPeriodLabel(point.period, groupBy, { short: true })}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>

            <div className="sr-point">
                <p>
                    <span className="sr-kicker">Periodo</span>
                    <strong>{formatPeriodLabel(active.period, groupBy)}</strong>
                </p>
                <p>
                    <span className="sr-kicker">{metric.label}</span>
                    <strong>{formatValue(active[metric.id])}</strong>
                </p>
                <p>
                    <span className="sr-kicker">Pedidos</span>
                    <strong>{formatCount(active.orderCount)}</strong>
                </p>
                <p>
                    <span className="sr-kicker">Devoluciones</span>
                    <strong>{formatMoney(active.returnTotal)}</strong>
                </p>
            </div>

            <table className="visually-hidden">
                <caption>Serie del reporte</caption>
                <thead>
                    <tr>
                        <th>Periodo</th>
                        <th>Neto</th>
                        <th>Bruto</th>
                        <th>Pedidos</th>
                        <th>Unidades</th>
                    </tr>
                </thead>
                <tbody>
                    {series.map((point) => (
                        <tr key={point.period}>
                            <td>{formatPeriodLabel(point.period, groupBy)}</td>
                            <td>{formatMoney(point.netSales)}</td>
                            <td>{formatMoney(point.grossSales)}</td>
                            <td>{formatCount(point.orderCount)}</td>
                            <td>{formatCount(point.unitsSold)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
    );
};

const formatCompactMoney = (value) => {
    const amount = Number(value) || 0;
    const abs = Math.abs(amount);
    if (abs >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (abs >= 1000) return `$${(amount / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
    return formatMoney(amount);
};

export default SalesReportChart;
